import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faSave,
  faEye,
  faEyeSlash,
  faTimes,
  faList,
  faInfoCircle,
  faCircleInfo,
  faInfo
} from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip, Modal, Button } from 'react-bootstrap';
import { createPractice, updatePractice, fetchPractice } from '../../../../store/slices/practiceSlice';
import useEmailNotifications from '../../../../hooks/useEmailNotifications';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import { toast } from 'react-toastify';
import { validatePassword } from '../../../../utils/passwordValidation';
import { US_STATES, TITLES, TIME_ZONES, normalizeTimeZone } from '../../../../utils/constants';
import FormDetailSkeleton from '../../../../components/SkeletonLoader/FormDetailSkeleton';
import practiceService from '../../../../services/practiceService';
import { resolvePracticeLogoUrl } from '../../../../utils/assetUrls';
import { getCalendarMonthPeriod } from '../../../../utils/billingPeriodUtils';


const AddPractice = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { isCreating, isUpdating, error, currentPractice } = useSelector(state => state.practice ?? {});
  const { user } = useSelector(state => state.auth ?? {});
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user && Number(user.role_id) === 3) {
      navigate(`/practices/${user.practice_id || ''}`, { replace: true });
    }
  }, [user, navigate]);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameAvailability, setUsernameAvailability] = useState({
    available: null,
    message: ''
  });
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailAvailability, setEmailAvailability] = useState({
    available: null,
    message: ''
  });

  // Helper for phone formatting (XXX) XXX - XXXX
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)} - ${phoneNumber.slice(6, 10)}`;
  };

  // Helper for email validation
  const validateEmail = (email) => {
    return /^[a-zA-Z0-9]+@[a-zA-Z0-9]+\.[a-zA-Z]+$/.test(email);
  };

  const getSafeUiErrorMessage = (rawMessage) => {
    const msg = String(rawMessage || '').trim();
    if (!msg) return 'Something went wrong. Please try again.';
    const lowered = msg.toLowerCase();
    if (
      lowered.includes('sql') ||
      lowered.includes('stack') ||
      lowered.includes('er_') ||
      lowered.includes('constraint')
    ) {
      return 'Unable to save practice right now. Please check form fields and try again.';
    }
    return msg;
  };

  const dedupeMonitoringTypeIds = (arr) => {
    if (!Array.isArray(arr)) return [];
    const seen = new Set();
    const out = [];
    for (const raw of arr) {
      const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
      if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
      seen.add(n);
      out.push(n);
    }
    return out;
  };

  const buildPracticePayloadFromForm = (data, logoValue) => ({
    practice_name: data.practice_name,
    practice_logo: logoValue,
    // Primary Contact
    address_1: data.primary_contact.facility_1,
    address_line_2: data.primary_contact.facility_2,
    city: data.primary_contact.city,
    state: data.primary_contact.state_id,
    zip_code: data.primary_contact.zip_code,
    phone_1: data.primary_contact.phone_1,
    email_1: data.primary_contact.email,
    fax_1: data.primary_contact.fax,
    // Secondary Contact
    address_2: data.secondary_contact.facility_1,
    address_2_line_2: data.secondary_contact.facility_2,
    city_1: data.secondary_contact.city,
    state_1: data.secondary_contact.state_id,
    zip_code_1: data.secondary_contact.zip_code,
    phone_2: data.primary_contact.phone_2,
    phone_secondary_1: data.secondary_contact.phone_1,
    phone_secondary_2: data.secondary_contact.phone_2,
    email_2: data.secondary_contact.email || '',
    fax_2: data.secondary_contact.fax || '',
    buying_listing_device: Boolean(data.monitoring.buying_device),
    monitor_to_patient: Boolean(data.monitoring.monitor_patients),
    leasing_listing_device: Boolean(data.monitoring.leasing_device),
    monitoring_types: data.monitoring.monitoring_types || '',
    selected_monitoring_types: dedupeMonitoringTypeIds(data.monitoring.selected_monitoring_types || []),
    account_number: data.account_number || null,
    billing_from_date: data.billing_rpm_start_date,
    billing_to_date: data.billing_rpm_end_date,
    enrolled_date: data.enrolled_date,
    rpm_start_date: data.billing_rpm_start_date,
    rpm_end_date: data.billing_rpm_end_date,
    rpm_validity_days: data.days || null,
    notes: data.billing_cycle_notes || null,
    status: data.status || 'Active',
    email_notifications_for_provider: Boolean(data.email_notifications.provider),
    email_notifications_for_practice_caregiver: Boolean(data.email_notifications.practice_caregiver),
    email_notifications_for_system_caregiver: Boolean(data.email_notifications.system_caregiver),
    email_notifications_for_superadmin: Boolean(data.email_notifications.superadmin),
    admin_details: {
      title: data.admin.title,
      first_name: data.admin.first_name,
      middle_initial: data.admin.middle_initial,
      last_name: data.admin.last_name,
      username: data.admin.username,
      password: data.admin.password,
      two_way_auth: data.admin.two_way_auth,
      address_line_1: data.admin.address_line_1,
      address_line_2: data.admin.address_line_2,
      city: data.admin.city,
      state: data.admin.state,
      zip_code: data.admin.zip_code,
      phone_1: data.admin.phone_1,
      phone_2: data.admin.phone_2,
      email: data.admin.email,
      fax: data.admin.fax,
      state_id: data.admin.state_id,
      time_zone: data.admin.time_zone
    }
  });

  const normalizeIsoDateToYmd = (value) => {
    if (!value) return null;
    const s = String(value);
    if (s.includes('T')) return s.split('T')[0];
    return s;
  };

  const getStateNameById = (id) => {
    if (!id) return '';
    const stateObj = US_STATES.find((s) => s.id === parseInt(id, 10));
    return stateObj ? stateObj.name : '';
  };

  const buildPracticePayloadFromCurrentPractice = (practice) => ({
    practice_name: practice?.practice_name || '',
    practice_logo: practice?.practice_logo || null,
    address_1: practice?.primary_address?.address_line_1 || practice?.address_line_1 || practice?.address_1 || '',
    address_line_2: practice?.primary_address?.address_line_2 || practice?.address_line_2 || '',
    city: practice?.primary_address?.city || practice?.city || '',
    state: practice?.primary_address?.state_id || practice?.state_id || practice?.state || null,
    zip_code: practice?.primary_address?.zip_code || practice?.zip_code || '',
    phone_1: practice?.primary_address?.phone || practice?.phone_1 || '',
    email_1: practice?.primary_address?.email || practice?.email_1 || '',
    fax_1: practice?.primary_address?.fax || practice?.fax_1 || '',
    address_2: practice?.secondary_address?.address_line_1 || practice?.address_2 || '',
    address_2_line_2: practice?.secondary_address?.address_line_2 || practice?.address_2_line_2 || '',
    city_1: practice?.secondary_address?.city || practice?.city_1 || '',
    state_1: practice?.secondary_address?.state_id || practice?.state_1 || null,
    zip_code_1: practice?.secondary_address?.zip_code || practice?.zip_code_1 || '',
    phone_2: practice?.primary_address?.cell_phone || practice?.phone_2 || '',
    phone_secondary_1: practice?.secondary_address?.phone || practice?.phone_secondary_1 || '',
    phone_secondary_2: practice?.secondary_address?.cell_phone || practice?.phone_secondary_2 || '',
    email_2: practice?.secondary_address?.email || practice?.email_2 || '',
    fax_2: practice?.secondary_address?.fax || practice?.fax_2 || '',
    buying_listing_device: practice?.buying_listing_device === 1 || practice?.buying_listing_device === true,
    monitor_to_patient: practice?.monitor_to_patient === 1 || practice?.monitor_to_patient === true,
    leasing_listing_device: practice?.leasing_listing_device === 1 || practice?.leasing_listing_device === true,
    monitoring_types: '',
    selected_monitoring_types: practice?.selected_monitoring_types || [],
    account_number: practice?.account_number || null,
    billing_from_date: normalizeIsoDateToYmd(practice?.billing_from_date || practice?.rpm_start_date || null),
    billing_to_date: normalizeIsoDateToYmd(practice?.billing_to_date || practice?.rpm_end_date || null),
    enrolled_date: normalizeIsoDateToYmd(practice?.enrolled_date || null),
    rpm_start_date: normalizeIsoDateToYmd(practice?.rpm_start_date || null),
    rpm_end_date: normalizeIsoDateToYmd(practice?.rpm_end_date || null),
    rpm_validity_days: practice?.rpm_validity_days || null,
    notes: practice?.notes || null,
    status: practice?.status || 'Active',
    email_notifications_for_provider: practice?.email_notifications_for_provider === 1 || practice?.email_notifications_for_provider === true,
    email_notifications_for_practice_caregiver: practice?.email_notifications_for_practice_caregiver === 1 || practice?.email_notifications_for_practice_caregiver === true,
    email_notifications_for_system_caregiver: practice?.email_notifications_for_system_caregiver === 1 || practice?.email_notifications_for_system_caregiver === true,
    email_notifications_for_superadmin: practice?.email_notifications_for_superadmin === 1 || practice?.email_notifications_for_superadmin === true,
    admin_details: {
      title: practice?.admin_title || '',
      first_name: practice?.admin_first_name || practice?.admin_details?.first_name || '',
      middle_initial: practice?.admin_middle_name || practice?.admin_details?.middle_name || '',
      last_name: practice?.admin_last_name || practice?.admin_details?.last_name || '',
      username: practice?.admin_username || practice?.admin_details?.username || '',
      password: '',
      two_way_auth: (practice?.admin_is_two_way_auth === 1 || practice?.admin_details?.is_two_way_auth === 1) ? 'Enabled' : 'Disabled',
      address_line_1: practice?.admin_details?.address_line_1 || '',
      address_line_2: practice?.admin_details?.address_line_2 || '',
      city: practice?.admin_details?.city || '',
      state: getStateNameById(practice?.admin_details?.state_id),
      zip_code: practice?.admin_details?.zip_code || '',
      phone_1: practice?.admin_phone || practice?.admin_details?.phone || '',
      phone_2: practice?.admin_cell_phone || practice?.admin_details?.cell_phone || '',
      email: practice?.admin_email || practice?.admin_details?.email || '',
      fax: practice?.admin_fax || practice?.admin_details?.fax || '',
      state_id: practice?.admin_details?.state_id || null,
      time_zone: normalizeTimeZone(
        practice?.admin_details?.time_zone
        || practice?.admin_details?.timeZone
        || practice?.admin_time_zone
      )
    }
  });

  const getChangedFields = (originalObj, updatedObj) => {
    // File objects have no enumerable keys; treat any File value as changed.
    if (updatedObj instanceof File) {
      return updatedObj;
    }
    if (Array.isArray(updatedObj)) {
      return JSON.stringify(originalObj || []) === JSON.stringify(updatedObj) ? undefined : updatedObj;
    }
    if (updatedObj === null || typeof updatedObj !== 'object') {
      return JSON.stringify(originalObj) === JSON.stringify(updatedObj) ? undefined : updatedObj;
    }

    const changed = {};
    Object.keys(updatedObj).forEach((key) => {
      const diff = getChangedFields(originalObj ? originalObj[key] : undefined, updatedObj[key]);
      if (diff !== undefined) {
        changed[key] = diff;
      }
    });

    return Object.keys(changed).length > 0 ? changed : undefined;
  };

  const areValuesEquivalentForUpdate = (key, originalValue, updatedValue) => {
    if (key === 'status' || key === 'practice_status') {
      return normalizePracticeStatus(originalValue) === normalizePracticeStatus(updatedValue);
    }
    if (key === 'selected_monitoring_types') {
      const lhs = dedupeMonitoringTypeIds(Array.isArray(originalValue) ? originalValue : []).slice().sort((a, b) => a - b);
      const rhs = dedupeMonitoringTypeIds(Array.isArray(updatedValue) ? updatedValue : []).slice().sort((a, b) => a - b);
      return JSON.stringify(lhs) === JSON.stringify(rhs);
    }
    if (key === 'monitoring_types') {
      return String(originalValue || '').trim() === String(updatedValue || '').trim();
    }
    if (key === 'admin_details') {
      const normalizeAdmin = (v) => ({
        ...v,
        // Empty password in edit mode should not force admin update payload.
        password: String(v?.password || '').trim(),
        state: String(v?.state || '').trim(),
        zip_code: String(v?.zip_code || '').trim(),
        time_zone: normalizeTimeZone(v?.time_zone || v?.timeZone || '')
      });
      const lhs = normalizeAdmin(originalValue || {});
      const rhs = normalizeAdmin(updatedValue || {});
      return JSON.stringify(lhs) === JSON.stringify(rhs);
    }
    return JSON.stringify(originalValue) === JSON.stringify(updatedValue);
  };

  const toFormDataFromPayload = (payload) => {
    const fd = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (typeof value === 'undefined') return;
      if (value instanceof File) {
        fd.append(key, value);
        return;
      }
      if (value === null) {
        fd.append(key, '');
        return;
      }
      if (Array.isArray(value) || typeof value === 'object') {
        fd.append(key, JSON.stringify(value));
        return;
      }
      fd.append(key, value);
    });
    return fd;
  };

  // Email notifications hook
  const {
    error: emailError,
    success: emailSuccess,
    sendPracticeCreationNotifications
  } = useEmailNotifications();

  // Check if we're in edit mode
  const editId = searchParams.get('editId');
  const isEditMode = Boolean(editId);

  // Monitoring types options for the monitoring details section
  const monitoringTypesOptions = [
    { value: 1, label: 'Blood Pressure' },
    { value: 2, label: 'Blood Glucose' },
    { value: 3, label: 'Weight' }
  ];

  const currentCalendarMonth = getCalendarMonthPeriod();

  const initialFormData = {
    // Practice Details
    practice_name: '',
    status: 'Active',
    enrolled_date: '',
    practice_logo: null,
    billing_cycle_notes: '',
    billing_rpm_start_date: currentCalendarMonth.dateFrom,
    days: String(currentCalendarMonth.daysInMonth),
    billing_rpm_end_date: currentCalendarMonth.dateTo,

    // Email Notifications
    email_notifications: {
      superadmin: false,
      provider: false,
      practice_caregiver: false,
      system_caregiver: false
    },

    // Primary Contact (Updated with Email/Fax)
    primary_contact: {
      facility_1: '',
      facility_2: '',
      city: '',
      state: '',
      state_id: null,
      zip_code: '',
      phone_1: '',
      phone_2: '',
      email: '', // NEW mandatory field
      fax: '' // NEW optional field
    },

    // Secondary Contact (Updated with Email/Fax)
    secondary_contact: {
      facility_1: '',
      facility_2: '',
      city: '',
      state: '',
      state_id: null,
      zip_code: '',
      phone_1: '',
      phone_2: '',
      email: '', // NEW optional field
      fax: '' // NEW optional field
    },

    // Monitoring Details
    monitoring: {
      monitor_patients: null,
      monitoring_types: '', // Text area for description
      selected_monitoring_types: [], // Selected monitoring types
      buying_device: null,
      leasing_device: null
    },

    // Admin Details
    admin: {
      title: '',
      first_name: '',
      middle_initial: '',
      last_name: '',
      username: '',
      password: '',
      confirm_password: '',
      two_way_auth: 'Disabled',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      state_id: null,
      zip_code: '',
      phone_1: '',
      phone_2: '',
      email: '',
      fax: '',
      time_zone: ''
    }
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState(null); // For preview of uploaded logo
  const [existingLogoUrl, setExistingLogoUrl] = useState(null); // For existing logo in edit mode
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showLockConfirmationModal, setShowLockConfirmationModal] = useState(false);
  const [lockConfirmationAccepted, setLockConfirmationAccepted] = useState(false);
  const [showUnlockConfirmationModal, setShowUnlockConfirmationModal] = useState(false);
  const [unlockConfirmationAccepted, setUnlockConfirmationAccepted] = useState(false);

  const normalizePracticeStatus = (status) => {
    if (status == null) return '';
    if (typeof status === 'number') {
      if (status === 4) return 'locked';
      if (status === 3) return 'pending';
      if (status === 2) return 'active';
    }
    return String(status).trim().toLowerCase();
  };

  const isTransitioningToLocked = () => {
    if (!isEditMode || !currentPractice) return false;
    const previousStatus = normalizePracticeStatus(currentPractice.status);
    const nextStatus = normalizePracticeStatus(formData.status);
    return previousStatus !== 'locked' && nextStatus === 'locked';
  };

  const isTransitioningFromLocked = () => {
    if (!isEditMode || !currentPractice) return false;
    const previousStatus = normalizePracticeStatus(currentPractice.status);
    const nextStatus = normalizePracticeStatus(formData.status);
    return previousStatus === 'locked' && nextStatus !== 'locked';
  };

  // Fetch practice data when in edit mode

  // Fetch practice data when in edit mode
  useEffect(() => {
    if (isEditMode && editId) {
      dispatch(fetchPractice(editId));
    }
  }, [isEditMode, editId, dispatch]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  useEffect(() => {
    updateBreadcrumbs([
      { label: 'Practices', path: '/practices' },
      { label: isEditMode ? 'Edit Practice' : 'Add Practice', path: null, active: true }
    ]);
  }, [isEditMode, updateBreadcrumbs]);

  // Populate form data when practice is loaded
  useEffect(() => {
    if (isEditMode && currentPractice) {
      console.log('🔍 Practice loaded for editing (Full Data):', currentPractice);

      // Set existing logo URL for preview
      if (currentPractice.practice_logo) {
        setExistingLogoUrl(resolvePracticeLogoUrl(currentPractice.practice_logo, {
          cacheBust: currentPractice.updated_at || currentPractice.id || 'practice-logo'
        }));
      } else {
        setExistingLogoUrl(null);
      }

      // Helper to convert State ID to Full Name
      const getStateName = (id) => {
        if (!id) return '';
        const stateObj = US_STATES.find(s => s.id === parseInt(id));
        return stateObj ? stateObj.name : '';
      };

      // Helper to convert Admin Title ID to String
      const getTitleString = (id) => {
        const titleObj = TITLES.find(t => t.id === parseInt(id));
        return titleObj ? titleObj.label : (TITLES.find(t => t.label === id)?.label || '');
      };

      setFormData(prev => ({
        ...prev,
        // Practice Details
        practice_name: currentPractice.practice_name || '',
        status: currentPractice.status || 'Active',
        enrolled_date: currentPractice.enrolled_date ? currentPractice.enrolled_date.split('T')[0] : '',
        practice_logo: null, // File object - will be null in edit mode unless user uploads new one
        billing_cycle_notes: currentPractice.notes || '',
        ...(() => {
          const ref =
            currentPractice.rpm_start_date ||
            currentPractice.billing_from_date ||
            currentCalendarMonth.dateFrom;
          const period = getCalendarMonthPeriod(String(ref).split('T')[0]);
          return {
            billing_rpm_start_date: period.dateFrom,
            billing_rpm_end_date: period.dateTo,
            days: String(period.daysInMonth)
          };
        })(),

        // Email Notifications — backend stores as email_notifications_for_* (with 's' + 'for_')
        email_notifications: {
          superadmin: currentPractice.email_notifications_for_superadmin === 1 || currentPractice.email_notification_superadmin === 1,
          provider: currentPractice.email_notifications_for_provider === 1 || currentPractice.email_notification_provider === 1,
          practice_caregiver: currentPractice.email_notifications_for_practice_caregiver === 1 || currentPractice.email_notification_practice_caregiver === 1,
          system_caregiver: currentPractice.email_notifications_for_system_caregiver === 1 || currentPractice.email_notification_system_caregiver === 1
        },

        // Primary Contact
        primary_contact: {
          facility_1: currentPractice.primary_address?.address_line_1 || currentPractice.address_line_1 || currentPractice.address_1 || '',
          facility_2: currentPractice.primary_address?.address_line_2 || currentPractice.address_line_2 || '',
          city: currentPractice.primary_address?.city || currentPractice.city || '',
          state: getStateName(currentPractice.primary_address?.state_id || currentPractice.state_id || currentPractice.state),
          state_id: currentPractice.primary_address?.state_id || currentPractice.state_id || currentPractice.state || null,
          zip_code: currentPractice.primary_address?.zip_code || currentPractice.zip_code || '',
          phone_1: currentPractice.primary_address?.phone || currentPractice.phone_1 || '',
          phone_2: currentPractice.primary_address?.cell_phone || currentPractice.phone_2 || '',
          email: currentPractice.primary_address?.email || currentPractice.email_1 || '',
          fax: currentPractice.primary_address?.fax || currentPractice.fax_1 || ''
        },

        // Secondary Contact
        secondary_contact: {
          facility_1: currentPractice.secondary_address?.address_line_1 || currentPractice.address_2 || '',
          facility_2: currentPractice.secondary_address?.address_line_2 || currentPractice.address_2_line_2 || '',
          city: currentPractice.secondary_address?.city || currentPractice.city_1 || '',
          state: getStateName(currentPractice.secondary_address?.state_id || currentPractice.state_1),
          state_id: currentPractice.secondary_address?.state_id || currentPractice.state_1 || null,
          zip_code: currentPractice.secondary_address?.zip_code || currentPractice.zip_code_1 || '',
          phone_1: currentPractice.secondary_address?.phone || currentPractice.phone_secondary_1 || '',
          phone_2: currentPractice.secondary_address?.cell_phone || currentPractice.phone_secondary_2 || '',
          email: currentPractice.secondary_address?.email || currentPractice.email_2 || '',
          fax: currentPractice.secondary_address?.fax || currentPractice.fax_2 || ''
        },

        // Monitoring Details
        monitoring: {
          monitor_patients: currentPractice.monitor_to_patient === 1,
          monitoring_types: '',
          selected_monitoring_types: currentPractice.selected_monitoring_types || [],
          buying_device: currentPractice.buying_listing_device === 1,
          leasing_device: currentPractice.leasing_listing_device === 1
        },

        // Admin Details — use flattened root-level fields AND nested admin_details as fallback
        admin: {
          title: getTitleString(currentPractice.admin_title || currentPractice.admin_details?.title_id),
          first_name: currentPractice.admin_first_name || currentPractice.admin_details?.first_name || '',
          middle_initial: currentPractice.admin_middle_name || currentPractice.admin_details?.middle_name || '',
          last_name: currentPractice.admin_last_name || currentPractice.admin_details?.last_name || '',
          username: currentPractice.admin_username || currentPractice.admin_details?.username || '',
          // Password not populated for security
          password: '',
          confirm_password: '',
          two_way_auth: (currentPractice.admin_is_two_way_auth === 1 || currentPractice.admin_details?.is_two_way_auth === 1) ? 'Enabled' : 'Disabled',
          address_line_1: currentPractice.admin_details?.address_line_1 || '',
          address_line_2: currentPractice.admin_details?.address_line_2 || '',
          city: currentPractice.admin_details?.city || '',
          state: getStateName(currentPractice.admin_details?.state_id),
          state_id: currentPractice.admin_details?.state_id || null,
          zip_code: currentPractice.admin_details?.zip_code || '',
          phone_1: currentPractice.admin_phone || currentPractice.admin_details?.phone || '',
          phone_2: currentPractice.admin_cell_phone || currentPractice.admin_details?.cell_phone || '',
          email: currentPractice.admin_email || currentPractice.admin_details?.email || '',
          fax: currentPractice.admin_fax || currentPractice.admin_details?.fax || '',
          time_zone: normalizeTimeZone(
            currentPractice.admin_details?.time_zone
            || currentPractice.admin_details?.timeZone
            || currentPractice.admin_time_zone
          )
        }
      }));
    }
  }, [isEditMode, currentPractice]);

  const hasEditChanges = useMemo(() => {
    if (!isEditMode || !currentPractice) return true;
    const logoValue = formData.practice_logo instanceof File
      ? formData.practice_logo
      : currentPractice.practice_logo;
    const currentPayload = buildPracticePayloadFromForm(formData, logoValue);
    const originalPayload = buildPracticePayloadFromCurrentPractice(currentPractice);
    const changedPayload = getChangedFields(originalPayload, currentPayload) || {};
    if (changedPayload.admin_details && Object.keys(changedPayload.admin_details).length === 0) {
      delete changedPayload.admin_details;
    }
    return Object.keys(changedPayload).length > 0;
  }, [isEditMode, currentPractice, formData, existingLogoUrl]);

  useEffect(() => {
    const username = String(formData.admin.username || '').trim();
    const currentAdminUsername = String(currentPractice?.admin_username || currentPractice?.admin_details?.username || '').trim();

    if (!username) {
      setUsernameAvailability({ available: null, message: '' });
      setIsCheckingUsername(false);
      return;
    }

    if (isEditMode && currentAdminUsername && username.toLowerCase() === currentAdminUsername.toLowerCase()) {
      setUsernameAvailability({ available: true, message: '' });
      setIsCheckingUsername(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setIsCheckingUsername(true);
        const response = await practiceService.checkAdminUsername(username, isEditMode ? editId : null);
        const available = Boolean(response?.data?.available);
        if (cancelled) return;
        setUsernameAvailability({
          available,
          message: available ? '' : 'Admin username already exists'
        });
        setErrors((prev) => {
          const next = { ...prev };
          if (available) {
            delete next['admin.username'];
          } else {
            next['admin.username'] = 'Admin username already exists';
          }
          return next;
        });
      } catch (err) {
        if (cancelled) return;
        setUsernameAvailability({ available: null, message: '' });
      } finally {
        if (!cancelled) setIsCheckingUsername(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.admin.username, isEditMode, editId, currentPractice]);

  useEffect(() => {
    const email = String(formData.admin.email || '').trim();
    const currentAdminEmail = String(
      currentPractice?.admin_email || currentPractice?.admin_details?.email || ''
    ).trim();

    if (!email) {
      setEmailAvailability({ available: null, message: '' });
      setIsCheckingEmail(false);
      return;
    }

    if (!validateEmail(email)) {
      setEmailAvailability({ available: null, message: '' });
      setIsCheckingEmail(false);
      return;
    }

    if (isEditMode && currentAdminEmail && email.toLowerCase() === currentAdminEmail.toLowerCase()) {
      setEmailAvailability({ available: true, message: '' });
      setIsCheckingEmail(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setIsCheckingEmail(true);
        const response = await practiceService.checkAdminEmail(email, isEditMode ? editId : null);
        const available = Boolean(response?.data?.available);
        if (cancelled) return;
        setEmailAvailability({
          available,
          message: available ? '' : 'Email already exists'
        });
        setErrors((prev) => {
          const next = { ...prev };
          if (available) {
            delete next['admin.email'];
          } else {
            next['admin.email'] = 'Email already exists';
          }
          return next;
        });
      } catch {
        if (cancelled) return;
        setEmailAvailability({ available: null, message: '' });
      } finally {
        if (!cancelled) setIsCheckingEmail(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.admin.email, isEditMode, editId, currentPractice]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'status') {
      setLockConfirmationAccepted(false);
      setUnlockConfirmationAccepted(false);
    }
    let newErrors = { ...errors };
    const requiredFieldLabels = {
      practice_name: 'Practice name',
      status: 'Status',
      enrolled_date: 'Enrolled date',
      billing_rpm_start_date: 'Billing RPM start date',
      billing_rpm_end_date: 'Billing RPM end date',
      days: 'Days',
      'primary_contact.facility_1': 'Primary facility',
      'primary_contact.city': 'Primary city',
      'primary_contact.state': 'Primary state',
      'primary_contact.zip_code': 'Primary zip code',
      'primary_contact.phone_1': 'Primary phone 1',
      'primary_contact.email': 'Primary contact email',
      'admin.first_name': 'Admin first name',
      'admin.last_name': 'Admin last name',
      'admin.username': 'Admin username',
      'admin.time_zone': 'Admin time zone',
      'admin.email': 'Admin email',
      'admin.address_line_1': 'Admin address',
      'admin.city': 'Admin city',
      'admin.state': 'Admin state',
      'admin.zip_code': 'Admin zip code',
      'admin.phone_1': 'Admin phone 1'
    };

    // Clear error for this field
    delete newErrors[name];

    // --- Checkbox/Radio Handling (Move to top to avoid interference) ---
    let processedValue = value;
    if (type === 'checkbox') {
      processedValue = checked;
      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        setFormData(prev => ({
          ...prev,
          [parent]: { ...prev[parent], [child]: processedValue }
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: processedValue }));
      }

      // Real-time validation for email notifications
      if (name.startsWith('email_notifications.')) {
        const [_, key] = name.split('.');
        const updatedNotifications = {
          ...formData.email_notifications,
          [key]: processedValue
        };
        const atLeastOne = Object.values(updatedNotifications).some(v => v === true);
        if (!atLeastOne) {
          newErrors.email_notifications = 'At least one email notification must be selected';
        } else {
          delete newErrors.email_notifications;
        }
        setErrors(newErrors);
      }

      return; // Stop here for checkboxes
    }

    if (type === 'radio') {
      processedValue = value === 'true';
    }

    // --- Validation Logic ---

    // 1. Phone Number Masking & Validation
    if (name.includes('phone') || name.includes('fax')) {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) { // Limit to 10 digits
        const formattedValue = formatPhoneNumber(numericValue);

        // Update form data with formatted value
        if (name.includes('.')) {
          const [section, field] = name.split('.');
          setFormData(prev => ({
            ...prev,
            [section]: { ...prev[section], [field]: formattedValue }
          }));
        } else {
          setFormData(prev => ({ ...prev, [name]: formattedValue }));
        }

        // --- Real-time Validation ---
        if (numericValue.length > 0 && numericValue.length < 10) {
          newErrors[name] = 'Invalid phone format';
        } else {
          delete newErrors[name];
        }
        if (requiredFieldLabels[name] && numericValue.length === 0) {
          newErrors[name] = `${requiredFieldLabels[name]} is required`;
        }
        setErrors(newErrors);
      }
      return; // Stop here as we handled the update
    }

    // 1.1 Zip Code Validation (Prevention)
    if (name.includes('zip_code')) {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) { // Limit to 5 digits as per user requirement
        if (name.includes('.')) {
          const [section, field] = name.split('.');
          setFormData(prev => ({
            ...prev,
            [section]: { ...prev[section], [field]: numericValue }
          }));
        } else {
          setFormData(prev => ({ ...prev, [name]: numericValue }));
        }

        // --- Real-time Validation ---
        if (numericValue.length > 0 && numericValue.length < 2) {
          newErrors[name] = 'Zip code must be at least 2 characters';
        } else if (requiredFieldLabels[name] && numericValue.length === 0) {
          newErrors[name] = `${requiredFieldLabels[name]} is required`;
        } else {
          delete newErrors[name];
        }
        setErrors(newErrors);
      }
      return;
    }

    // 2. Strict Name Validation (Prevention)
    // For First Name, Last Name, Middle Initial: letters only, no spaces for middle initial
    if (['admin.first_name', 'admin.last_name', 'admin.middle_initial'].includes(name)) {
      // Allow letters and spaces (we will show warning for spaces in Middle Name if needed)
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }

      // Special check for Middle Name spaces (per user request: show warning)
      if (name === 'admin.middle_initial') {
        if (value.includes(' ')) {
          newErrors['admin.middle_initial'] = 'Middle Initial cannot contain spaces';
        } else {
          delete newErrors['admin.middle_initial'];
        }
      }

      // Keep 20-char limit for first/last name only
      if ((name === 'admin.first_name' || name === 'admin.last_name') && value.length > 20) {
        return; // BLOCK if > 20
      }
    }

    // For Practice Name: Max 50 characters EXCLUDING spaces AND No special characters
    if (name === 'practice_name') {
      // Check for allowed characters (Letters and spaces only)
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return; // BLOCK update if contains invalid char (number, special char)
      }

      const charCount = value.replace(/\s/g, '').length;
      if (charCount > 50) {
        return; // BLOCK if > 50 chars (excluding spaces)
      }
    }

    // For City, Title: Letters, spaces, and dots (for titles)
    if (name.includes('city') || (name.includes('title') && !name.includes('facility'))) {
      if (value && !/^[a-zA-Z\s.]*$/.test(value)) {
        return;
      }
    }

    // Facility fields: allow letters, numbers, spaces, and common address punctuation
    if (name.includes('facility')) {
      if (value && !/^[a-zA-Z0-9\s.#\-,/]*$/.test(value)) {
        return;
      }
      if (value.length > 0 && value.length < 3) {
        newErrors[name] = 'Facility must be at least 3 characters';
      } else if (name === 'primary_contact.facility_1' && !value) {
        newErrors[name] = 'Primary facility is required';
      } else {
        delete newErrors[name];
      }
      setErrors(newErrors);
    }

    // 3. Email Validation (Blocking spaces + Instant validation)
    if (name.includes('email') && type !== 'checkbox') {
      const cleanedValue = value.replace(/[^a-zA-Z0-9@.]/g, '');

      // 3.1 Update Form Data (Strip spaces)
      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        setFormData(prev => ({
          ...prev,
          [parent]: { ...prev[parent], [child]: cleanedValue }
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: cleanedValue }));
      }

      // 3.2 Instant Validation
      if (cleanedValue && !validateEmail(cleanedValue)) {
        newErrors[name] = 'Invalid email format';
      } else if (requiredFieldLabels[name] && !cleanedValue.trim()) {
        newErrors[name] = `${requiredFieldLabels[name]} is required`;
      } else {
        delete newErrors[name];
      }

      setErrors(newErrors);
      return; // Handled update
    }

    // 3.1 State sync
    if (name.includes('state')) {
      const stateObj = US_STATES.find(s => s.name === value);
      const section = name.includes('.') ? name.split('.')[0] : null;
      if (stateObj) {
        if (section) {
          setFormData(prev => ({ ...prev, [section]: { ...prev[section], state_id: stateObj.id } }));
        } else {
          setFormData(prev => ({ ...prev, state_id: stateObj.id }));
        }
        delete newErrors[name];
      } else {
        if (section) {
          setFormData(prev => ({ ...prev, [section]: { ...prev[section], state_id: null } }));
        } else {
          setFormData(prev => ({ ...prev, state_id: null }));
        }
      }
    }

    // 4. Calendar-month billing period (1st through last day of selected month)
    if (name === 'billing_rpm_start_date') {
      if (value) {
        const { dateFrom, dateTo, daysInMonth } = getCalendarMonthPeriod(value);
        setFormData((prev) => ({
          ...prev,
          billing_rpm_start_date: dateFrom,
          billing_rpm_end_date: dateTo,
          days: String(daysInMonth)
        }));
        delete newErrors.billing_rpm_start_date;
        delete newErrors.billing_rpm_end_date;
        delete newErrors.days;
      } else {
        setFormData((prev) => ({
          ...prev,
          billing_rpm_end_date: '',
          days: ''
        }));
      }
    }

    // Real-time Password validation removed per user request (only on click)
    if (requiredFieldLabels[name]) {
      const emptyValue = processedValue === null || processedValue === undefined || String(processedValue).trim() === '';
      if (emptyValue) {
        newErrors[name] = name === 'days' ? 'Days are required' : `${requiredFieldLabels[name]} is required`;
      } else if (!newErrors[name]) {
        delete newErrors[name];
      }
    }

    setErrors(newErrors);

    // --- Standard Update Logic for other fields ---
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: processedValue
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);

      setFormData(prev => ({
        ...prev,
        practice_logo: file
      }));
      // Clear existing logo URL when new file is selected
      setExistingLogoUrl(null);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setExistingLogoUrl(null);
    setFormData(prev => ({
      ...prev,
      practice_logo: null
    }));

    // Reset file input
    const fileInput = document.getElementById('practice_logo');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // --- Practice Details validation (No change)
    if (!formData.practice_name) {
      newErrors.practice_name = 'Practice name is required';
    }
    if (!formData.enrolled_date) {
      newErrors.enrolled_date = 'Enrolled date is required';
    }
    if (!formData.billing_rpm_start_date) {
      newErrors.billing_rpm_start_date = 'Billing RPM start date is required';
    }
    if (!formData.days) {
      newErrors.days = 'Days are required';
    }
    if (!formData.billing_rpm_end_date) {
      newErrors.billing_rpm_end_date = 'Billing RPM end date is required';
    } else if (formData.billing_rpm_start_date && formData.billing_rpm_end_date) {
      const startDate = new Date(formData.billing_rpm_start_date);
      const endDate = new Date(formData.billing_rpm_end_date);
      if (endDate < startDate) {
        newErrors.billing_rpm_end_date = 'End Date cannot be before Start Date';
      }
    }
    const practiceStatus = formData.status || 'Active';
    if (!practiceStatus) {
      newErrors.status = 'Status is required';
    }
    if (!formData.monitoring.selected_monitoring_types || formData.monitoring.selected_monitoring_types.length === 0) {
      newErrors['monitoring.selected_monitoring_types'] = 'At least one monitoring type must be selected';
    }

    // --- Email notifications validation (No change)
    const hasEmailNotification = Object.values(formData.email_notifications).some(val => val);
    if (!hasEmailNotification) {
      newErrors.email_notifications = 'At least one email notification must be selected';
    }

    // --- Primary contact validation (UPDATED)
    if (!formData.primary_contact.facility_1) {
      newErrors['primary_contact.facility_1'] = 'Primary facility is required';
    }
    if (!formData.primary_contact.city) {
      newErrors['primary_contact.city'] = 'Primary city is required';
    }
    if (!formData.primary_contact.state) {
      newErrors['primary_contact.state'] = 'Primary state is required';
    }
    if (!formData.primary_contact.zip_code) {
      newErrors['primary_contact.zip_code'] = 'Primary zip code is required';
    } else if (formData.primary_contact.zip_code.length < 2) {
      newErrors['primary_contact.zip_code'] = 'Zip code must be at least 2 characters';
    }
    if (!formData.primary_contact.phone_1) {
      newErrors['primary_contact.phone_1'] = 'Primary phone 1 is required';
    }
    // New: Primary Contact Email is MANDATORY
    if (!formData.primary_contact.email) {
      newErrors['primary_contact.email'] = 'Primary contact email is required';
    } else if (!validateEmail(formData.primary_contact.email)) {
      newErrors['primary_contact.email'] = 'Invalid email format';
    }

    // --- Secondary Contact validation
    if (formData.secondary_contact.facility_1 && formData.secondary_contact.facility_1.length < 3) {
      newErrors['secondary_contact.facility_1'] = 'Facility must be at least 3 characters';
    }
    if (formData.secondary_contact.facility_2 && formData.secondary_contact.facility_2.length < 3) {
      newErrors['secondary_contact.facility_2'] = 'Facility must be at least 3 characters';
    }
    if (formData.secondary_contact.phone_1 && formData.secondary_contact.phone_1.replace(/\D/g, '').length < 10) {
      newErrors['secondary_contact.phone_1'] = 'Invalid phone format';
    }
    if (formData.secondary_contact.phone_2 && formData.secondary_contact.phone_2.replace(/\D/g, '').length < 10) {
      newErrors['secondary_contact.phone_2'] = 'Invalid phone format';
    }
    if (formData.secondary_contact.email && !validateEmail(formData.secondary_contact.email)) {
      newErrors['secondary_contact.email'] = 'Invalid email format';
    }
    if (formData.secondary_contact.zip_code && formData.secondary_contact.zip_code.length < 2) {
      newErrors['secondary_contact.zip_code'] = 'Zip code must be at least 2 characters';
    }

    // --- Middle Name space check ---
    if (formData.admin.middle_initial && /\s/.test(formData.admin.middle_initial)) {
      newErrors['admin.middle_initial'] = 'Middle Initial cannot contain spaces';
    }

    // --- Admin details validation (No change)
    if (!formData.admin.first_name) {
      newErrors['admin.first_name'] = 'Admin first name is required';
    }
    if (!formData.admin.last_name) {
      newErrors['admin.last_name'] = 'Admin last name is required';
    }
    if (!formData.admin.username) {
      newErrors['admin.username'] = 'Admin username is required';
    } else if (usernameAvailability.available === false) {
      newErrors['admin.username'] = 'Admin username already exists';
    }

    // Password Validation
    if (!isEditMode) {
      if (!formData.admin.password) {
        newErrors['admin.password'] = 'Admin password is required';
      } else {
        const userData = {
          firstName: formData.admin.first_name,
          lastName: formData.admin.last_name
        };
        const validation = validatePassword(formData.admin.password, userData);
        if (!validation.isValid) {
          newErrors['admin.password'] = validation.error;
        }
      }
    } else if (formData.admin.password) {
      const userData = {
        firstName: formData.admin.first_name,
        lastName: formData.admin.last_name
      };
      const validation = validatePassword(formData.admin.password, userData);
      if (!validation.isValid) {
        newErrors['admin.password'] = validation.error;
      }
    }
    if (formData.admin.password && formData.admin.password !== formData.admin.confirm_password) {
      newErrors['admin.confirm_password'] = 'Passwords do not match';
    }
    if (!formData.admin.time_zone) {
      newErrors['admin.time_zone'] = 'Admin time zone is required';
    }

    // Email Notifications validation
    const atLeastOneNotification = Object.values(formData.email_notifications).some(v => v === true);
    if (!atLeastOneNotification) {
      newErrors.email_notifications = 'At least one email notification must be selected';
    }
    if (!formData.admin.email) {
      newErrors['admin.email'] = 'Admin email is required';
    } else if (!validateEmail(formData.admin.email)) {
      newErrors['admin.email'] = 'Invalid email format';
    } else if (emailAvailability.available === false) {
      newErrors['admin.email'] = 'Email already exists';
    }
    if (!formData.admin.address_line_1) {
      newErrors['admin.address_line_1'] = 'Admin address is required';
    }
    if (!formData.admin.city) {
      newErrors['admin.city'] = 'Admin city is required';
    }
    if (!formData.admin.state) {
      newErrors['admin.state'] = 'Admin state is required';
    }
    if (!formData.admin.zip_code) {
      newErrors['admin.zip_code'] = 'Admin zip code is required';
    } else if (formData.admin.zip_code.length < 2) {
      newErrors['admin.zip_code'] = 'Zip code must be at least 2 characters';
    }
    if (!formData.admin.phone_1) {
      newErrors['admin.phone_1'] = 'Admin phone 1 is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper to completely disable space key
  const handleNoSpaceKeyDown = (e) => {
    if (e.key === ' ' || e.keyCode === 32) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isTransitioningToLocked() && !lockConfirmationAccepted) {
      setShowLockConfirmationModal(true);
      return;
    }
    if (isTransitioningFromLocked() && !unlockConfirmationAccepted) {
      setShowUnlockConfirmationModal(true);
      return;
    }

    // Clear any previous errors
    setFormError('');
    setIsSubmitting(true);

    if (isCheckingUsername) {
      setIsSubmitting(false);
      setFormError('Please wait for username validation to finish.');
      return;
    }

    if (isCheckingEmail) {
      setIsSubmitting(false);
      setFormError('Please wait for email validation to finish.');
      return;
    }

    if (!validateForm()) {
      setIsSubmitting(false);
      setShowValidationModal(true);
      return;
    }

    try {
      const isFileUpload = formData.practice_logo instanceof File;
      let practiceData;

      const logoValue = isEditMode
        ? (isFileUpload ? formData.practice_logo : currentPractice?.practice_logo)
        : formData.practice_logo;
      const currentPayload = buildPracticePayloadFromForm(formData, logoValue);

      if (isEditMode && currentPractice) {
        const originalPayload = buildPracticePayloadFromCurrentPractice(currentPractice);
        const changedPayload = getChangedFields(originalPayload, currentPayload) || {};

        Object.keys(changedPayload).forEach((key) => {
          if (areValuesEquivalentForUpdate(key, originalPayload[key], currentPayload[key])) {
            delete changedPayload[key];
          }
        });

        if (changedPayload.admin_details) {
          const adminChanged = !areValuesEquivalentForUpdate(
            'admin_details',
            originalPayload.admin_details,
            currentPayload.admin_details
          );
          const hasNewAdminPassword = String(currentPayload?.admin_details?.password || '').trim().length > 0;
          const nonAdminChangedKeys = Object.keys(changedPayload).filter((k) => k !== 'admin_details');
          const statusOrMonitoringOnlyUpdate =
            nonAdminChangedKeys.length > 0 &&
            nonAdminChangedKeys.every((k) =>
              ['status', 'practice_status', 'selected_monitoring_types', 'monitoring_types'].includes(k)
            );

          if ((!adminChanged && !hasNewAdminPassword) || (statusOrMonitoringOnlyUpdate && !hasNewAdminPassword)) {
            delete changedPayload.admin_details;
          } else {
            changedPayload.admin_details = currentPayload.admin_details;
          }
        }

        if (Object.keys(changedPayload).length === 0) {
          toast.info('No changes to update');
          setIsSubmitting(false);
          return;
        }
        practiceData = isFileUpload ? toFormDataFromPayload(changedPayload) : changedPayload;
      } else {
        // create mode keeps full payload
        practiceData = isFileUpload ? toFormDataFromPayload(currentPayload) : currentPayload;
      }

      // Debug: Log the full data being sent
      if (practiceData instanceof FormData) {
        console.log('🔍 Sending FormData to backend:');
        for (let [key, value] of practiceData.entries()) {
          console.log(`${key}:`, value instanceof File ? `File (${value.name})` : value);
        }
      } else {
        console.log('🔍 Sending JSON to backend:', practiceData);
      }

      console.log('🔍 Submission Info:', {
        formStatus: formData.status,
        editId: editId,
        isEditMode
      });

      let result;
      if (isEditMode) {
        try {
          result = await dispatch(updatePractice({ id: editId, practiceData }));

          if (updatePractice.fulfilled.match(result)) {
            console.log('✅ Update Practice response:', result.payload);
            toast.success('Practice updated successfully');
            await dispatch(fetchPractice(editId));
            navigate(`/practices/${editId}`, { state: { practiceUpdated: true } });
          } else if (updatePractice.rejected.match(result)) {
            console.error('Error updating practice:', result.payload);
            setFormError(getSafeUiErrorMessage(result.payload || 'Failed to update practice. Please try again.'));
          } else {
            console.error('Unexpected result type:', result.type);
            setFormError('An unexpected error occurred. Please try again.');
          }
        } catch (updateError) {
          console.error('Exception during update:', updateError);
          setFormError('An error occurred while updating the practice. Please try again.');
        } finally {
          setIsSubmitting(false);
        }
      } else {
        result = await dispatch(createPractice(practiceData));
        if (createPractice.fulfilled.match(result)) {
          console.log('✅ Create Practice response:', result.payload);
          // Send email notifications after successful practice creation
          try {
            const notificationSettings = {
              email_notifications_for_superadmin: Boolean(formData.email_notifications.superadmin),
              email_notifications_for_provider: Boolean(formData.email_notifications.provider),
              email_notifications_for_practice_caregiver: Boolean(formData.email_notifications.practice_caregiver),
              email_notifications_for_system_caregiver: Boolean(formData.email_notifications.system_caregiver)
            };

            const emailResult = await sendPracticeCreationNotifications(
              result.payload.data.practice,
              result.payload.data.admin,
              notificationSettings
            );

            if (emailResult.success) {
            } else {
              console.warn('⚠️ Some email notifications failed:', emailResult.error);
            }
          } catch (emailError) {
            console.error('❌ Email notification error:', emailError);
            // Don't fail the practice creation if emails fail
          }

          // Navigate to the created practice detail page
          // The response structure is: { success: true, data: { id: practiceId, ... }, message: '...' }
          const createdPracticeId = result.payload?.data?.id;
          if (createdPracticeId) {
            setTimeout(() => {
              navigate(`/practices/${createdPracticeId}`);
            }, 1500);
          } else {
            // Fallback to practices list if ID not available
            console.warn('Practice ID not found in response, navigating to practices list');
            setTimeout(() => {
              navigate('/practices');
            }, 1500);
          }
        } else if (createPractice.rejected.match(result)) {
          console.error('Error creating practice:', result.payload);
          console.error('Full error response:', result);

          // Show the most relevant error message - ensure it's always a string
          let errorMessage = 'Failed to create practice. Please try again.';
          if (result.payload) {
            if (typeof result.payload === 'string') {
              errorMessage = result.payload;
            } else if (result.payload.message && typeof result.payload.message === 'string') {
              errorMessage = result.payload.message;
            }
          }
          const normalizedMessage = String(errorMessage || '').toLowerCase();
          const duplicatePracticeNameError =
            normalizedMessage.includes('practice with this name already exists') ||
            (normalizedMessage.includes('already exists') && normalizedMessage.includes('practice'));

          if (duplicatePracticeNameError) {
            setErrors((prev) => ({
              ...prev,
              practice_name: 'Practice name already exists. Use a different name.'
            }));
            setFormError('');
          } else {
            setFormError(getSafeUiErrorMessage(errorMessage));
          }
        }
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      setFormError('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    } finally {
      setLockConfirmationAccepted(false);
      setUnlockConfirmationAccepted(false);
    }
  };

  const handleConfirmLock = () => {
    setShowLockConfirmationModal(false);
    setLockConfirmationAccepted(true);
    const form = document.querySelector('.add-practice-provider-form');
    if (form && typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    }
  };

  const handleConfirmUnlock = () => {
    setShowUnlockConfirmationModal(false);
    setUnlockConfirmationAccepted(true);
    const form = document.querySelector('.add-practice-provider-form');
    if (form && typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  const handleCancel = () => {
    navigate('/practices');
  };

  const focusPracticeNameField = useCallback(() => {
    const practiceNameInput = document.getElementById('practice_name');
    if (!practiceNameInput) return;
    practiceNameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      practiceNameInput.focus();
    }, 150);
  }, []);

  const topLevelErrorMessage =
    formError || (typeof error === 'string' ? error : error?.message || '');
  const isDuplicatePracticeNameError = (() => {
    const normalized = String(topLevelErrorMessage || '').toLowerCase();
    return (
      normalized.includes('practice with this name already exists') ||
      (normalized.includes('already exists') && normalized.includes('practice'))
    );
  })();

  useEffect(() => {
    if (!isDuplicatePracticeNameError) return;
    setErrors((prev) => ({
      ...prev,
      practice_name: prev.practice_name || 'Practice name already exists. Use a different name.'
    }));
    // Keep duplicate-name message only at field level.
    if (formError) {
      setFormError('');
    }
    focusPracticeNameField();
  }, [isDuplicatePracticeNameError, formError, focusPracticeNameField]);

  useEffect(() => {
    if (!errors.practice_name) return;
    focusPracticeNameField();
  }, [errors.practice_name, focusPracticeNameField]);

  const isEditPracticeLoading =
    isEditMode &&
    (!currentPractice || String(currentPractice.id) !== String(editId));

  if (isEditPracticeLoading) {
    return (
      <div className="add-practice-container">
        <div className="add-practice-form-card" style={{ padding: '16px', display: 'grid', gap: '14px' }}>
          <FormDetailSkeleton titleLines={1} fieldRows={12} columns={2} showFooter={false} />
          <FormDetailSkeleton titleLines={1} fieldRows={5} columns={2} showFooter={false} />
          <FormDetailSkeleton titleLines={1} fieldRows={2} columns={2} showFooter={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="add-practice-container">
      <div className="add-practice-form-card">
        <div className="add-practice-form-header add-practice-d-flex add-practice-justify-content-between add-practice-align-items-center">
          <div className="d-flex align-items-center gap-3">
            <h4>{isEditMode ? 'Edit Practice' : 'Add Practice'}</h4>

          </div>
          <button
            className="add-practice-btn-back"
            onClick={handleCancel}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="add-practice-me-2" /> Back
          </button>
        </div>

        <form onSubmit={handleSubmit} className="add-practice-provider-form" noValidate>
          {/* Error Display */}
          {topLevelErrorMessage && !isDuplicatePracticeNameError && (
            <div className="add-practice-alert add-practice-alert-danger">
              {getSafeUiErrorMessage(topLevelErrorMessage || 'An error occurred')}
            </div>
          )}

          {/* Email Notification Status */}
          {emailError && <div className="add-practice-alert add-practice-alert-warning">{emailError}</div>}

          {/* ----- Practice Details ----- */}
          <div className="add-practice-section-card">
            <h5 className="add-practice-section-header">Practice Details</h5>
            {/* Row Pair 1: Practice Name, Status, Enrolled Date, Practice Logo */}
            <div className="add-practice-row-pair">
              <div className="add-practice-col-quarter">
                <div className="add-practice-form-group">
                  <label htmlFor="practice_name">
                    Practice Name <span className="add-practice-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="practice_name"
                    name="practice_name"
                    value={formData.practice_name}
                    onChange={handleInputChange}
                    className={`add-practice-form-control ${errors.practice_name ? 'is-invalid' : ''}`}

                  />
                  {errors.practice_name && <div className="add-practice-error-message">{errors.practice_name}</div>}
                </div>
              </div>

              <div className="add-practice-col-quarter">
                <div className="add-practice-form-group">
                  <label htmlFor="status">
                    Status <span className="add-practice-required">*</span>
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status || 'Active'}
                    onChange={handleInputChange}
                    className="add-practice-form-control"
                  >
                    <option value="Active">Active</option>
                    <option value="Pending">Pending</option>
                    <option value="Locked">Locked</option>
                  </select>
                  {errors.status && <div className="add-practice-error-message">{errors.status}</div>}
                </div>
              </div>

              <div className="add-practice-col-quarter">
                <div className="add-practice-form-group">
                  <label htmlFor="enrolled_date">
                    Enrolled Date <span className="add-practice-required">*</span>
                  </label>
                  <input
                    type="date"
                    id="enrolled_date"
                    name="enrolled_date"
                    value={formData.enrolled_date}
                    onChange={handleInputChange}
                    className="add-practice-form-control"

                  />
                  {errors.enrolled_date && <div className="add-practice-error-message">{errors.enrolled_date}</div>}
                </div>
              </div>

              <div className="add-practice-col-quarter">
                <div className="add-practice-form-group">
                  <label htmlFor="practice_logo">Practice Logo</label>
                  <div className="add-practice-file-input-wrapper">
                    <div className="add-practice-custom-file-input" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={() => document.getElementById('practice_logo').click()}
                        className="add-practice-btn add-practice-btn-secondary"
                      >
                        Choose File
                      </button>
                      <span style={{ fontSize: '14px', color: '#6c757d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px' }} title={formData.practice_logo instanceof File ? formData.practice_logo.name : (existingLogoUrl ? existingLogoUrl.split('/').pop() : 'No file chosen')}>
                        {formData.practice_logo instanceof File ? formData.practice_logo.name : (existingLogoUrl ? existingLogoUrl.split('/').pop() : 'No file chosen')}
                      </span>
                      <input
                        type="file"
                        id="practice_logo"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="add-practice-form-control"
                        style={{ display: 'none' }} // Hide default input
                      />
                    </div>

                    {(existingLogoUrl || logoPreview) && (
                      <div className="add-practice-logo-preview-container" style={{ position: 'relative', display: 'inline-flex', width: 'fit-content', marginTop: '10px' }}>
                        <img
                          src={logoPreview || existingLogoUrl}
                          alt="Preview"
                          className="add-practice-logo-preview"
                          style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd', display: 'block' }}
                        />
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            background: '#dc3545',
                            color: 'white',
                            border: '2px solid white',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            zIndex: 10,
                            padding: 0,
                            fontSize: '10px'
                          }}
                          title="Remove logo"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row Pair 2: Billing Notes, Start Date, Days, End Date */}
            <div className="add-practice-row-pair">
              <div className="add-practice-col-quarter">
                <div className="add-practice-form-group" style={{ height: '100%' }}>
                  <label htmlFor="billing_cycle_notes">Billing Cycle Notes</label>
                  <textarea
                    id="billing_cycle_notes"
                    name="billing_cycle_notes"
                    value={formData.billing_cycle_notes}
                    onChange={handleInputChange}
                    className="add-practice-form-control"
                    style={{ minHeight: '42px', height: 'auto', resize: 'vertical' }}
                    rows="1"
                  />
                </div>
              </div>

              <div className="add-practice-col-quarter">
                <div className="add-practice-form-group">
                  <label htmlFor="billing_rpm_start_date">
                    Billing month (1st of month) <span className="add-practice-required">*</span>
                  </label>
                  <input
                    type="date"
                    id="billing_rpm_start_date"
                    name="billing_rpm_start_date"
                    value={formData.billing_rpm_start_date}
                    onChange={handleInputChange}
                    className="add-practice-form-control"

                  />
                  {errors.billing_rpm_start_date && <div className="add-practice-error-message">{errors.billing_rpm_start_date}</div>}
                </div>
              </div>

              <div className="add-practice-col-quarter">
                <div className="add-practice-form-group">
                  <label htmlFor="days">
                    Days in month <span className="add-practice-required">*</span>
                  </label>
                  <input
                    type="number"
                    id="days"
                    name="days"
                    value={formData.days}
                    readOnly
                    className="add-practice-form-control"
                    min="1"
                  />
                  {errors.days && <div className="add-practice-error-message">{errors.days}</div>}
                </div>
              </div>

              <div className="add-practice-col-quarter">
                <div className="add-practice-form-group">
                  <label htmlFor="billing_rpm_end_date">
                    Billing month end (last day) <span className="add-practice-required">*</span>
                  </label>
                  <input
                    type="date"
                    id="billing_rpm_end_date"
                    name="billing_rpm_end_date"
                    value={formData.billing_rpm_end_date}
                    readOnly
                    className="add-practice-form-control"
                  />
                  {errors.billing_rpm_end_date && <div className="add-practice-error-message">{errors.billing_rpm_end_date}</div>}
                </div>
              </div>
            </div>
          </div>

          {/* ----- Email Notifications ----- */}
          <div className="add-practice-section-card">
            <h5 className="add-practice-section-header">Email Notifications</h5>
            <div className="add-practice-form-group add-practice-indented-section" style={{ marginBottom: '20px' }}>
              <label className="add-practice-monitoring-section-label" style={{ fontWeight: '600', marginBottom: '10px', display: 'block' }}>
                Add Patient <span className="add-practice-required">*</span>
              </label>
              <div className="add-practice-monitoring-options">
                {Object.entries(formData.email_notifications).map(([key, value]) => (
                  <div key={key} className="add-practice-checkbox-group">
                    <input
                      type="checkbox"
                      id={`email_notifications.${key}`}
                      name={`email_notifications.${key}`}
                      checked={value}
                      onChange={handleInputChange}
                    />
                    <label htmlFor={`email_notifications.${key}`}>
                      {key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </label>
                  </div>
                ))}
              </div>
              {errors.email_notifications && <div className="add-practice-error-message">{errors.email_notifications}</div>}
            </div>
          </div>

          {/* ----- Contact Information ----- */}
          <div className="add-practice-section-card">
            <h5 className="add-practice-section-header-main">Contact Information</h5>
            <div className="add-practice-contacts-wrapper">

              {/* PRIMARY CONTACT */}
              <div className="add-practice-contact-panel">
                <p className="add-practice-contact-panel-title">Primary Contact</p>

                {/* Row 1: Facility 1 | Facility 2 | City | State */}
                <div className="add-practice-contact-row add-practice-contact-row-4">
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>Facility 1 <span className="add-practice-required">*</span></label>
                      <input type="text" name="primary_contact.facility_1" value={formData.primary_contact.facility_1} onChange={handleInputChange} className={`add-practice-form-control ${errors['primary_contact.facility_1'] ? 'is-invalid' : ''}`} />
                      {errors['primary_contact.facility_1'] && <div className="add-practice-error-message">{errors['primary_contact.facility_1']}</div>}
                    </div>
                  </div>
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>Facility 2</label>
                      <input type="text" name="primary_contact.facility_2" value={formData.primary_contact.facility_2} onChange={handleInputChange} className={`add-practice-form-control ${errors['primary_contact.facility_2'] ? 'is-invalid' : ''}`} />
                      {errors['primary_contact.facility_2'] && <div className="add-practice-error-message">{errors['primary_contact.facility_2']}</div>}
                    </div>
                  </div>
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>City <span className="add-practice-required">*</span></label>
                      <input type="text" name="primary_contact.city" value={formData.primary_contact.city} onChange={handleInputChange} className={`add-practice-form-control ${errors['primary_contact.city'] ? 'is-invalid' : ''}`} />
                      {errors['primary_contact.city'] && <div className="add-practice-error-message">{errors['primary_contact.city']}</div>}
                    </div>
                  </div>
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>State <span className="add-practice-required">*</span></label>
                      <select name="primary_contact.state" value={formData.primary_contact.state} onChange={handleInputChange} className={`add-practice-form-control ${errors['primary_contact.state'] ? 'is-invalid' : ''}`}>
                        <option value="">Select State</option>
                        {US_STATES.map(state => <option key={state.id} value={state.name}>{state.name}</option>)}
                      </select>
                      {errors['primary_contact.state'] && <div className="add-practice-error-message">{errors['primary_contact.state']}</div>}
                    </div>
                  </div>
                </div>

                {/* Row 2: Phone 1 | Phone 2 | Zip Code | Email */}
                <div className="add-practice-contact-row add-practice-contact-row-4">
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>
                        Phone 1 <span className="add-practice-required">*</span>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                          <FontAwesomeIcon icon={faInfo} className="field-info-icon" />
                        </OverlayTrigger>
                      </label>
                      <input type="tel" name="primary_contact.phone_1" value={formData.primary_contact.phone_1} onChange={handleInputChange} className="add-practice-form-control" />
                      {errors['primary_contact.phone_1'] && <div className="add-practice-error-message">{errors['primary_contact.phone_1']}</div>}
                    </div>
                  </div>
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>
                        Phone 2
                        <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                          <FontAwesomeIcon icon={faInfo} className="field-info-icon" />
                        </OverlayTrigger>
                      </label>
                      <input type="tel" name="primary_contact.phone_2" value={formData.primary_contact.phone_2} onChange={handleInputChange} className="add-practice-form-control" />
                    </div>
                  </div>
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>Zip Code <span className="add-practice-required">*</span></label>
                      <input type="text" name="primary_contact.zip_code" value={formData.primary_contact.zip_code} onChange={handleInputChange} className="add-practice-form-control" />
                      {errors['primary_contact.zip_code'] && <div className="add-practice-error-message">{errors['primary_contact.zip_code']}</div>}
                    </div>
                  </div>
                  <div className="add-practice-contact-col">
                    <div className="add-practice-contact-col">
                      <div className="add-practice-form-group">
                        <label>
                          Fax
                          <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                            <FontAwesomeIcon icon={faInfo} className="field-info-icon" />
                          </OverlayTrigger>
                        </label>
                        <input type="tel" name="primary_contact.fax" value={formData.primary_contact.fax} onChange={handleInputChange} className="add-practice-form-control" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 3: Fax (single field) */}
                <div className="add-practice-contact-row add-practice-contact-row-4">
                  <div className="add-practice-form-group">
                    <label>Email <span className="add-practice-required">*</span></label>
                    <input
                      type="email"
                      name="primary_contact.email"
                      value={formData.primary_contact.email}
                      onChange={handleInputChange}
                      onKeyDown={handleNoSpaceKeyDown}
                      className="add-practice-form-control"
                    />
                    {errors['primary_contact.email'] && <div className="add-practice-error-message">{errors['primary_contact.email']}</div>}
                  </div>

                </div>
              </div>

              {/* SECONDARY CONTACT */}
              <div className="add-practice-contact-panel">
                <p className="add-practice-contact-panel-title">Secondary Contact</p>

                {/* Row 1: Facility 1 | Facility 2 | City | State */}
                <div className="add-practice-contact-row add-practice-contact-row-4">
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>Facility 1</label>
                      <input type="text" name="secondary_contact.facility_1" value={formData.secondary_contact.facility_1} onChange={handleInputChange} className={`add-practice-form-control ${errors['secondary_contact.facility_1'] ? 'is-invalid' : ''}`} />
                      {errors['secondary_contact.facility_1'] && <div className="add-practice-error-message">{errors['secondary_contact.facility_1']}</div>}
                    </div>
                  </div>
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>Facility 2</label>
                      <input type="text" name="secondary_contact.facility_2" value={formData.secondary_contact.facility_2} onChange={handleInputChange} className={`add-practice-form-control ${errors['secondary_contact.facility_2'] ? 'is-invalid' : ''}`} />
                      {errors['secondary_contact.facility_2'] && <div className="add-practice-error-message">{errors['secondary_contact.facility_2']}</div>}
                    </div>
                  </div>
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>City</label>
                      <input type="text" name="secondary_contact.city" value={formData.secondary_contact.city} onChange={handleInputChange} className={`add-practice-form-control ${errors['secondary_contact.city'] ? 'is-invalid' : ''}`} />
                      {errors['secondary_contact.city'] && <div className="add-practice-error-message">{errors['secondary_contact.city']}</div>}
                    </div>
                  </div>
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>State</label>
                      <select name="secondary_contact.state" value={formData.secondary_contact.state} onChange={handleInputChange} className={`add-practice-form-control ${errors['secondary_contact.state'] ? 'is-invalid' : ''}`}>
                        <option value="">Select State</option>
                        {US_STATES.map(state => <option key={state.id} value={state.name}>{state.name}</option>)}
                      </select>
                      {errors['secondary_contact.state'] && <div className="add-practice-error-message">{errors['secondary_contact.state']}</div>}
                    </div>
                  </div>
                </div>

                {/* Row 2: Phone 1 | Phone 2 | Zip Code | Email */}
                <div className="add-practice-contact-row add-practice-contact-row-4">
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>
                        Phone 1
                        <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                          <FontAwesomeIcon icon={faInfo} className="field-info-icon" />
                        </OverlayTrigger>
                      </label>
                      <input type="tel" name="secondary_contact.phone_1" value={formData.secondary_contact.phone_1} onChange={handleInputChange} className={`add-practice-form-control ${errors['secondary_contact.phone_1'] ? 'is-invalid' : ''}`} />
                      {errors['secondary_contact.phone_1'] && <div className="add-practice-error-message">{errors['secondary_contact.phone_1']}</div>}
                    </div>
                  </div>
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>
                        Phone 2
                        <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                          <FontAwesomeIcon icon={faInfo} className="field-info-icon" />
                        </OverlayTrigger>
                      </label>
                      <input type="tel" name="secondary_contact.phone_2" value={formData.secondary_contact.phone_2} onChange={handleInputChange} className={`add-practice-form-control ${errors['secondary_contact.phone_2'] ? 'is-invalid' : ''}`} />
                      {errors['secondary_contact.phone_2'] && <div className="add-practice-error-message">{errors['secondary_contact.phone_2']}</div>}
                    </div>
                  </div>
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>Zip Code</label>
                      <input type="text" name="secondary_contact.zip_code" value={formData.secondary_contact.zip_code} onChange={handleInputChange} className="add-practice-form-control" />
                    </div>
                  </div>
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        name="secondary_contact.email"
                        value={formData.secondary_contact.email}
                        onChange={handleInputChange}
                        onKeyDown={handleNoSpaceKeyDown}
                        className="add-practice-form-control"
                      />
                    </div>
                  </div>
                </div>

                {/* Row 3: Fax (single field) */}
                <div className="add-practice-contact-row add-practice-contact-row-4">
                  <div className="add-practice-contact-col">
                    <div className="add-practice-form-group">
                      <label>
                        Fax
                        <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                          <FontAwesomeIcon icon={faInfo} className="field-info-icon" />
                        </OverlayTrigger>
                      </label>
                      <input type="tel" name="secondary_contact.fax" value={formData.secondary_contact.fax} onChange={handleInputChange} className="add-practice-form-control" />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>



          {/* ----- Monitoring Details ----- */}

          <div className="add-practice-section-card">
            <h5 className="add-practice-section-header">Monitoring Details</h5>

            <div className="add-practice-form-group">
              <span className="add-practice-monitoring-section-label">Monitor patients?</span>
              <div className="add-practice-monitoring-options">
                <div className="add-practice-checkbox-group">
                  <input type="radio" name="monitoring.monitor_patients" value={true} checked={formData.monitoring.monitor_patients === true} onChange={handleInputChange} />
                  <label>Yes</label>
                </div>
                <div className="add-practice-checkbox-group">
                  <input type="radio" name="monitoring.monitor_patients" value={false} checked={formData.monitoring.monitor_patients === false} onChange={handleInputChange} />
                  <label>No</label>
                </div>
              </div>
            </div>

            <div className="add-practice-form-group" style={{ marginTop: '16px' }}>
              <span className="add-practice-monitoring-section-label add-practice-required">Monitoring Types</span>
              <div className="add-practice-monitoring-options">
                {monitoringTypesOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`add-practice-monitoring-option ${(formData.monitoring.selected_monitoring_types || []).includes(option.value) ? 'selected' : ''}`}
                    onClick={() => {
                      const currentTypes = formData.monitoring.selected_monitoring_types || [];
                      const newTypes = currentTypes.includes(option.value)
                        ? currentTypes.filter(type => type !== option.value)
                        : [...currentTypes, option.value];
                      setFormData(prev => ({
                        ...prev,
                        monitoring: { ...prev.monitoring, selected_monitoring_types: newTypes }
                      }));
                      if (newTypes.length > 0) {
                        setErrors(prev => {
                          const { 'monitoring.selected_monitoring_types': _, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                  >
                    <span>{option.label}</span>
                    {(formData.monitoring.selected_monitoring_types || []).includes(option.value) && (
                      <span style={{ marginLeft: '8px', fontSize: '12x', fontWeight: 'bold' }}>✓</span>
                    )}
                  </div>
                ))}
              </div>
              {errors['monitoring.selected_monitoring_types'] && <div className="add-practice-error-message">{errors['monitoring.selected_monitoring_types']}</div>}
            </div>

            <div style={{ display: 'flex', gap: '40px', marginTop: '20px', marginBottom: '16px' }}>
              <div style={{ flex: '0 0 auto' }}>
                <div className="add-practice-form-group">
                  <span className="add-practice-monitoring-section-label">Buying Device?</span>
                  <div className="add-practice-monitoring-options">
                    <div className="add-practice-checkbox-group">
                      <input type="radio" name="monitoring.buying_device" value={true} checked={formData.monitoring.buying_device === true} onChange={handleInputChange} />
                      <label>Yes</label>
                    </div>
                    <div className="add-practice-checkbox-group">
                      <input type="radio" name="monitoring.buying_device" value={false} checked={formData.monitoring.buying_device === false} onChange={handleInputChange} />
                      <label>No</label>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ flex: '0 0 auto' }}>
                <div className="add-practice-form-group">
                  <span className="add-practice-monitoring-section-label">Leasing Device?</span>
                  <div className="add-practice-monitoring-options">
                    <div className="add-practice-checkbox-group">
                      <input type="radio" name="monitoring.leasing_device" value={true} checked={formData.monitoring.leasing_device === true} onChange={handleInputChange} />
                      <label>Yes</label>
                    </div>
                    <div className="add-practice-checkbox-group">
                      <input type="radio" name="monitoring.leasing_device" value={false} checked={formData.monitoring.leasing_device === false} onChange={handleInputChange} />
                      <label>No</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ----- Admin Details ----- */}
          <div className="add-practice-section-card">
            <h5 className="add-practice-section-header">Admin Details</h5>
            <div className="add-practice-admin-panel">


              {/* Row: Title + First Name | Middle + Last Name */}
              <div className="add-practice-contact-row">
                <div className="add-practice-contact-col" style={{ display: 'flex', gap: '10px' }}>
                  <div className="add-practice-form-group" style={{ flex: '0 0 130px' }}>
                    <label>Title</label>
                    <select name="admin.title" value={formData.admin.title} onChange={handleInputChange} className="add-practice-form-control">
                      <option value="">Select Title</option>
                      {TITLES.map(t => <option key={t.id} value={t.label}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="add-practice-form-group" style={{ flex: 1 }}>
                    <label>First Name <span className="add-practice-required">*</span></label>
                    <input type="text" name="admin.first_name" value={formData.admin.first_name} onChange={handleInputChange} className="add-practice-form-control" />
                    {errors['admin.first_name'] && <div className="add-practice-error-message">{errors['admin.first_name']}</div>}
                  </div>
                </div>
                <div className="add-practice-contact-col" style={{ display: 'flex', gap: '10px' }}>
                  <div className="add-practice-form-group" style={{ flex: '0 0 110px' }}>
                    <label>Middle Initial</label>
                    <input
                      type="text"
                      name="admin.middle_initial"
                      value={formData.admin.middle_initial}
                      onChange={handleInputChange}
                      onKeyDown={handleNoSpaceKeyDown}
                      className="add-practice-form-control"
                    />
                    {errors['admin.middle_initial'] && <div className="add-practice-error-message">{errors['admin.middle_initial']}</div>}
                  </div>
                  <div className="add-practice-form-group" style={{ flex: 1 }}>
                    <label>Last Name <span className="add-practice-required">*</span></label>
                    <input type="text" name="admin.last_name" value={formData.admin.last_name} onChange={handleInputChange} className="add-practice-form-control" />
                    {errors['admin.last_name'] && <div className="add-practice-error-message">{errors['admin.last_name']}</div>}
                  </div>
                </div>
              </div>

              {/* Row: Username | 2FA */}
              <div className="add-practice-contact-row">
                <div className="add-practice-contact-col">
                  <div className="add-practice-form-group">
                    <label>Username <span className="add-practice-required">*</span></label>
                    <input
                      type="text"
                      name="admin.username"
                      value={formData.admin.username}
                      onChange={handleInputChange}
                      onKeyDown={handleNoSpaceKeyDown}
                      className="add-practice-form-control"
                    />
                    {!errors['admin.username'] && isCheckingUsername && (
                      <div className="add-practice-form-hint">Checking username...</div>
                    )}
                    {errors['admin.username'] && <div className="add-practice-error-message">{errors['admin.username']}</div>}
                  </div>
                </div>
                <div className="add-practice-contact-col" style={{ display: 'flex', gap: '10px' }}>
                  <div className="add-practice-form-group" style={{ flex: 1 }}>
                    <label>Time Zone <span className="add-practice-required">*</span></label>
                    <select name="admin.time_zone" value={formData.admin.time_zone} onChange={handleInputChange} className="add-practice-form-control">
                      <option value="">Select Time Zone</option>
                      {TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                    </select>
                    {errors['admin.time_zone'] && <div className="add-practice-error-message">{errors['admin.time_zone']}</div>}
                  </div>
                  <div className="add-practice-form-group" style={{ flex: 1 }}>
                    <label>Two Way Authentication <span className="add-practice-required">*</span></label>
                    <select name="admin.two_way_auth" value={formData.admin.two_way_auth} onChange={handleInputChange} className="add-practice-form-control">
                      <option value="Disabled">Disabled</option>
                      <option value="Enabled">Enabled</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Row: Password | Confirm Password */}
              <div className="add-practice-contact-row">
                <div className="add-practice-contact-col">
                  <div className="add-practice-form-group">
                    <label>
                      Password {!isEditMode && <span className="add-practice-required">*</span>}
                      <OverlayTrigger placement="top" overlay={<Tooltip>8-64 characters, 1 uppercase, 1 lowercase, 1 number, 1 special char.</Tooltip>}>
                        <FontAwesomeIcon icon={faInfo} className="field-info-icon" />
                      </OverlayTrigger>
                    </label>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <input type={showPassword ? 'text' : 'password'} name="admin.password" value={formData.admin.password} onChange={handleInputChange} className="add-practice-form-control" style={{ paddingRight: '36px' }} />
                      <button type="button" onClick={togglePasswordVisibility} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#6c757d', cursor: 'pointer', padding: 0 }}>
                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                    {errors['admin.password'] && <div className="add-practice-error-message">{errors['admin.password']}</div>}
                  </div>
                </div>
                <div className="add-practice-contact-col">
                  <div className="add-practice-form-group">
                    <label>Confirm Password {!isEditMode && <span className="add-practice-required">*</span>}</label>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <input type={showConfirmPassword ? 'text' : 'password'} name="admin.confirm_password" value={formData.admin.confirm_password} onChange={handleInputChange} className="add-practice-form-control" style={{ paddingRight: '36px' }} />
                      <button type="button" onClick={toggleConfirmPasswordVisibility} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#6c757d', cursor: 'pointer', padding: 0 }}>
                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                    {errors['admin.confirm_password'] && <div className="add-practice-error-message">{errors['admin.confirm_password']}</div>}
                  </div>
                </div>
              </div>

              {/* Row: Address Line 1 | Address Line 2 */}
              <div className="add-practice-contact-row">
                <div className="add-practice-contact-col">
                  <div className="add-practice-form-group">
                    <label>Address Line 1 <span className="add-practice-required">*</span></label>
                    <input type="text" name="admin.address_line_1" value={formData.admin.address_line_1} onChange={handleInputChange} className="add-practice-form-control" />
                    {errors['admin.address_line_1'] && <div className="add-practice-error-message">{errors['admin.address_line_1']}</div>}
                  </div>
                </div>
                <div className="add-practice-contact-col">
                  <div className="add-practice-form-group">
                    <label>Address Line 2</label>
                    <input type="text" name="admin.address_line_2" value={formData.admin.address_line_2} onChange={handleInputChange} className="add-practice-form-control" />
                  </div>
                </div>
              </div>

              {/* Row: City | State */}
              <div className="add-practice-contact-row">
                <div className="add-practice-contact-col">
                  <div className="add-practice-form-group">
                    <label>City <span className="add-practice-required">*</span></label>
                    <input type="text" name="admin.city" value={formData.admin.city} onChange={handleInputChange} className="add-practice-form-control" />
                    {errors['admin.city'] && <div className="add-practice-error-message">{errors['admin.city']}</div>}
                  </div>
                </div>
                <div className="add-practice-contact-col">
                  <div className="add-practice-form-group">
                    <label>State <span className="add-practice-required">*</span></label>
                    <select name="admin.state" value={formData.admin.state} onChange={handleInputChange} className="add-practice-form-control">
                      <option value="">Select State</option>
                      {US_STATES.map(state => <option key={state.id} value={state.name}>{state.name}</option>)}
                    </select>
                    {errors['admin.state'] && <div className="add-practice-error-message">{errors['admin.state']}</div>}
                  </div>
                </div>
              </div>

              {/* Row: Zip Code | Phone 1 */}
              <div className="add-practice-contact-row">
                <div className="add-practice-contact-col">
                  <div className="add-practice-form-group">
                    <label>Zip Code <span className="add-practice-required">*</span></label>
                    <input type="text" name="admin.zip_code" value={formData.admin.zip_code} onChange={handleInputChange} className="add-practice-form-control" />
                    {errors['admin.zip_code'] && <div className="add-practice-error-message">{errors['admin.zip_code']}</div>}
                  </div>
                </div>
                <div className="add-practice-contact-col">
                  <div className="add-practice-form-group">
                    <label>
                      Phone 1 <span className="add-practice-required">*</span>
                      <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                        <FontAwesomeIcon icon={faInfo} className="field-info-icon" />
                      </OverlayTrigger>
                    </label>
                    <input type="tel" name="admin.phone_1" value={formData.admin.phone_1} onChange={handleInputChange} className="add-practice-form-control" />
                    {errors['admin.phone_1'] && <div className="add-practice-error-message">{errors['admin.phone_1']}</div>}
                  </div>
                </div>
              </div>

              {/* Row: Phone 2 | Email */}
              <div className="add-practice-contact-row">
                <div className="add-practice-contact-col">
                  <div className="add-practice-form-group">
                    <label>
                      Phone 2
                      <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                        <FontAwesomeIcon icon={faInfo} className="field-info-icon" />
                      </OverlayTrigger>
                    </label>
                    <input type="tel" name="admin.phone_2" value={formData.admin.phone_2} onChange={handleInputChange} className="add-practice-form-control" />
                  </div>
                </div>
                <div className="add-practice-contact-col">
                  <div className="add-practice-form-group">
                    <label>Email <span className="add-practice-required">*</span></label>
                    <input
                      type="email"
                      name="admin.email"
                      value={formData.admin.email}
                      onChange={handleInputChange}
                      onKeyDown={handleNoSpaceKeyDown}
                      className={`add-practice-form-control${errors['admin.email'] ? ' is-invalid' : ''}`}
                    />
                    {errors['admin.email'] && <div className="add-practice-error-message">{errors['admin.email']}</div>}
                  </div>
                </div>
              </div>

              {/* Row: Fax */}
              <div className="add-practice-contact-row">
                <div className="add-practice-contact-col">
                  <div className="add-practice-form-group">
                    <label>
                      Fax
                      <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                        <FontAwesomeIcon icon={faInfo} className="field-info-icon" />
                      </OverlayTrigger>
                    </label>
                    <input type="tel" name="admin.fax" value={formData.admin.fax} onChange={handleInputChange} className="add-practice-form-control" />
                  </div>
                </div>
                <div className="add-practice-contact-col"></div>
              </div>

            </div> {/* end add-practice-admin-panel */}
          </div>

          <div className="add-practice-form-actions" style={{ justifyContent: 'space-between' }}>
            {isEditMode ? (
              <button
                type="button"
                className="list-screen-option-btn"
                onClick={() => navigate(`/event-logs/Practice/${editId}?practiceId=${editId}`)}
                title="View Change History"
                style={{ width: 'auto', paddingLeft: '15px', paddingRight: '15px' }}
              >
                <FontAwesomeIcon icon={faList} className="add-practice-me-2" /> Events Log
              </button>
            ) : (
              <div></div>
            )}
            <div style={{ display: 'flex', gap: '15px' }}>
              <button
                type="button"
                className="add-practice-btn add-practice-btn-cancel"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="add-practice-btn add-practice-btn-add"
                disabled={isSubmitting || isCreating || isUpdating || isCheckingUsername || isCheckingEmail || (isEditMode && !hasEditChanges)}
              >
                {(isSubmitting || isCreating || isUpdating) ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="add-practice-me-2" />
                    {isEditMode ? 'Updating...' : 'Adding...'}
                  </>
                ) : (
                  <>
                    {isEditMode ? 'Update' : 'Add'}
                  </>
                )}
              </button>
            </div>
          </div>


        </form>
      </div>
      <Modal
        show={showValidationModal}
        onHide={() => setShowValidationModal(false)}
        centered
        className="add-practice-validation-modal"
      >
        <Modal.Body className="add-practice-modal-body" style={{ padding: '30px', textAlign: 'center' }}>
          <div className="add-practice-modal-icon-wrapper" style={{ marginBottom: '20px' }}>
            <FontAwesomeIcon icon={faInfoCircle} className="add-practice-modal-icon" style={{ fontSize: '40px', color: '#1A252F' }} />
          </div>
          <h4 className="add-practice-modal-title" style={{ color: '#1A252F', marginBottom: '20px', fontWeight: '600' }}>Fill all required fields</h4>
          <button
            type="button"
            className="add-practice-modal-btn"
            onClick={() => setShowValidationModal(false)}
            style={{ backgroundColor: '#1A252F', color: '#fff', border: 'none', padding: '10px 30px', borderRadius: '4px', cursor: 'pointer', width: '100%' }}
          >
            OK
          </button>
        </Modal.Body>
      </Modal>
      <Modal
        show={showLockConfirmationModal}
        onHide={() => setShowLockConfirmationModal(false)}
        centered
        className="add-practice-lock-modal"
        contentClassName="add-practice-lock-modal-content"
      >
        <Modal.Header
          closeButton
          style={{ background: '#2c2c2c', color: '#fff', borderBottom: 'none' }}
        >
          <Modal.Title as="h5" className="mb-0" style={{ fontWeight: 600 }}>
            Lock practice?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.25rem 1.5rem' }}>
          <p className="mb-2" style={{ color: '#2c3e50', fontSize: '0.95rem' }}>
            Are you sure you want to lock this practice? All users in this practice will be affected and access will be restricted.
          </p>
          <p className="mb-0 small text-muted">
            Patients keep read-only access to readings for 30 days; staff logins may be blocked until the practice is active again.
          </p>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #e9ecef', gap: 8 }}>
          <Button variant="light" className="border" onClick={() => setShowLockConfirmationModal(false)}>
            Cancel
          </Button>
          <Button style={{ background: '#2c2c2c', border: 'none' }} onClick={handleConfirmLock}>
            Yes, lock practice
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showUnlockConfirmationModal}
        onHide={() => setShowUnlockConfirmationModal(false)}
        centered
        className="add-practice-lock-modal"
        contentClassName="add-practice-lock-modal-content"
      >
        <Modal.Header
          closeButton
          style={{ background: '#2c2c2c', color: '#fff', borderBottom: 'none' }}
        >
          <Modal.Title as="h5" className="mb-0" style={{ fontWeight: 600 }}>
            Set practice to active?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.25rem 1.5rem' }}>
          <p className="mb-2" style={{ color: '#2c3e50', fontSize: '0.95rem' }}>
            This will unlock the practice for staff and restore normal access. An email notification will be sent to users.
          </p>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #e9ecef', gap: 8 }}>
          <Button variant="light" className="border" onClick={() => setShowUnlockConfirmationModal(false)}>
            Cancel
          </Button>
          <Button style={{ background: '#2c2c2c', border: 'none' }} onClick={handleConfirmUnlock}>
            Yes, activate practice
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AddPractice;